import {MediaActionType} from '../store/slices/media';
import {useAppDispatch, useAppSelector} from '../store/hooks';
import {play, stop, loaded} from '../store/slices/media';
import {
  downloadFileById,
  exportFile,
  getById,
  queryClient,
  removeObject,
  writeItem,
} from '../api';
import {setDownloadingProgress} from '../store/slices/fs';
import {Media} from '../types';
import {
  setAppDialogParams,
  setFolderMenuItemId,
} from '../store/slices/ui';
import {useMutation, useQuery, useQueryClient} from 'react-query';
import {useCloseDialog} from './useCloseDialog';

export const useMediaPlayer = (
  folderId?: string,
  folderContentQuery?: ReturnType<typeof useQuery>,
) => {
  const mediaState = useAppSelector(state => state.media);
  const {closeDialog} = useCloseDialog();
  const downloads = useAppSelector(
    state => state.fs.downloadingProgress,
  );
  const dispatch = useAppDispatch();

  const onMediaPress = async (media: Media) => {
    console.info('mediapress', media);
    try {
      const mediaId = media.id;
      dispatch(stop());
      if (mediaState.currentMediaId !== mediaId && mediaId !== '') {
        dispatch(
          play({actionType: MediaActionType.play, mediaId, media}),
        );
      }
    } catch (err) {
      console.error('mediapress error', err);
    }
  };
  const deleteMediaMutation = useMutation(async (mediaId: string) => {
    const media = (await getById(mediaId, folderId)) as Media;

    try {
      await removeObject(media);
    } catch (err) {
      console.error('removeObject error', err);
    }

    await writeItem({
      ...media,
      isDeleted: true,
    });
    await folderContentQuery?.refetch();
    await queryClient.refetchQueries('folderData_' + folderId);
  });

  const deleteFileMutation = useMutation(async (mediaId: string) => {
    const media = (await getById(mediaId, folderId)) as Media;
    try {
      await removeObject(media);
    } catch (err) {
      console.error('removeObject error', err);
    }
    await queryClient.refetchQueries(['folderData_' + folderId]);
    await queryClient.refetchQueries([
      'isDownloaded',
      mediaId,
      media.isDownloaded,
    ]);
  });
  const exportFileMutation = useMutation(async (mediaId: string) => {
    const media = (await getById(mediaId, folderId)) as Media;
    await exportFile(media);
  });

  const onRemoveMediaPress = (mediaId: string) => {
    dispatch(setFolderMenuItemId({itemId: undefined}));
    dispatch(
      setAppDialogParams({
        appDialogVisible: true,
        appDialogTitle: 'Delete Media',
        appDialogIcon: 'delete',
        appDialogOnSelectRight: async () => {
          await deleteMediaMutation.mutateAsync(mediaId);
          closeDialog();
        },
        appDialogOnSelectLeftt: undefined,
        appDialogBody:
          'This media will be removed for you and other users who may have access',
        appDialogControls: undefined,
        appDialogLoading: false,
      }),
    );
  };

  const onExportFile = (mediaId: string) => {
    dispatch(setFolderMenuItemId({itemId: undefined}));
    dispatch(
      setAppDialogParams({
        appDialogVisible: true,
        appDialogTitle: 'Export Media',
        appDialogIcon: 'share',
        appDialogOnSelectRight: async () => {
          await exportFileMutation.mutateAsync(mediaId);
          closeDialog();
        },
        appDialogOnSelectLeftt: undefined,
        appDialogBody:
          'This media will be unencrypted and exported to your device',
        appDialogControls: undefined,
        appDialogLoading: false,
      }),
    );
  };

  const onRemoveFile = (mediaId: string) => {
    dispatch(setFolderMenuItemId({itemId: undefined}));
    dispatch(
      setAppDialogParams({
        appDialogVisible: true,
        appDialogTitle: 'Delete Media',
        appDialogIcon: 'delete',
        appDialogOnSelectRight: async () => {
          await deleteFileMutation.mutateAsync(mediaId);
          closeDialog();
        },
        appDialogOnSelectLeftt: undefined,
        appDialogBody:
          'Only the media file will be removed and only from this device',
        appDialogControls: undefined,
        appDialogLoading: false,
      }),
    );
  };

  const onDownloadMediaPress = async (mediaId: string) => {
    console.info('onDownloadMediaPress', mediaId);
    const itemId = mediaId;
    if (downloads[itemId]) return;
    dispatch(setDownloadingProgress({itemId, progress: 0.001}));
    try {
      await downloadFileById(mediaId, folderId, progress =>
        dispatch(
          setDownloadingProgress({
            itemId,
            progress,
          }),
        ),
      );
    } catch (err) {
      console.error(err);
    }
    dispatch(
      setDownloadingProgress({itemId: mediaId, progress: undefined}),
    );
  };

  const onCopyLinkPress = (mediaId: string) => {
    console.info('onShareMediaPress', mediaId);
  };

  const onEditMediaPress = (mediaId: string) => {
    console.info('onEditMediaPress', mediaId);
  };

  return {
    ...mediaState,
    onMediaPress,
    onRemoveMediaPress,
    onDownloadMediaPress,
    onCopyLinkPress,
    onRemoveFile,
    onExportFile,
    onEditMediaPress,
  };
};
